/**
 * Corporate term journey: rider sum-assured bounds and validation.
 * Rules: product min/max per rider type; rider SA cannot exceed base plan SA.
 */

export const RIDER_ID_ADB = "accidental-death";
export const RIDER_ID_ADD = "accidental-death-disability";
export const RIDER_ID_ADD_PLUS = "accidental-death-disability-plus";
/** ADB rider SA cannot exceed this fraction of base sum assured (100% = same cap as base SA). */
export const RIDER_ADB_MAX_OF_BASE_SA_RATIO = 1;
export const RIDER_ID_CI = "critical-illness";

/** ADB / ADD / ADD+ — ₹50,000 to ₹1,00,00,000 */
export const RIDER_ADB_ADD_MIN = 50_000;
export const RIDER_ADB_ADD_MAX = 10_000_000;

/** CI — ₹1,00,000 to ₹50,00,000 */
export const RIDER_CI_MIN = 100_000;
export const RIDER_CI_MAX = 5_000_000;

/** Round rider SA to rupee step (manual entry; finer than base cover grid). */
export const RIDER_SA_RUPEE_STEP = 1_000;

export const getRiderProductBounds = (riderId) => {
  if (riderId === RIDER_ID_CI) {
    return { min: RIDER_CI_MIN, max: RIDER_CI_MAX };
  }
  if (
    riderId === RIDER_ID_ADB ||
    riderId === RIDER_ID_ADD ||
    riderId === RIDER_ID_ADD_PLUS
  ) {
    return { min: RIDER_ADB_ADD_MIN, max: RIDER_ADB_ADD_MAX };
  }
  return { min: 0, max: Number.POSITIVE_INFINITY };
};

/**
 * Effective bounds: product rules capped by base sum assured (rider SA ≤ base SA).
 * @param {string} riderId
 * @param {number|null|undefined} baseSumAssured
 * @returns {{ min: number, max: number }}
 */
export const getEffectiveRiderSaBounds = (riderId, baseSumAssured) => {
  const { min: pMin, max: pMax } = getRiderProductBounds(riderId);
  const base =
    baseSumAssured != null &&
    Number.isFinite(Number(baseSumAssured)) &&
    Number(baseSumAssured) > 0
      ? Number(baseSumAssured)
      : null;
  if (base == null) {
    return { min: pMin, max: pMax };
  }
  const effectiveProductMax =
    riderId === RIDER_ID_ADB
      ? Math.min(pMax, Math.floor(base * RIDER_ADB_MAX_OF_BASE_SA_RATIO))
      : pMax;
  return {
    min: pMin,
    max: Math.min(effectiveProductMax, base),
  };
};

export const quantizeRiderSa = (amount, min, max) => {
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) return null;
  const step = RIDER_SA_RUPEE_STEP;
  const rounded = Math.round(n / step) * step;
  return Math.min(max, Math.max(min, rounded));
};

/**
 * Parse + quantize for commit. Returns { ok, value, error }.
 * @param {string} rawInput
 * @param {(s: string) => number|null} parseFn - e.g. parseCoverAmountInput
 */
export const commitRiderCoverInput = (rawInput, riderId, baseSumAssured, parseFn) => {
  const { min, max } = getEffectiveRiderSaBounds(riderId, baseSumAssured);
  if (max < min) {
    return {
      ok: false,
      value: null,
      error:
        "Life cover amount is too low for this add-on’s minimum sum assured.",
    };
  }
  const trimmed = String(rawInput ?? "").trim();
  if (!trimmed) {
    return { ok: false, value: null, error: "Enter a cover amount." };
  }
  const parsed = parseFn(trimmed);
  if (parsed == null || !Number.isFinite(parsed)) {
    return {
      ok: false,
      value: null,
      error: "Enter a valid cover amount (e.g. 500000 or 5 lakh).",
    };
  }
  if (parsed < min || parsed > max) {
    return {
      ok: false,
      value: null,
      error: `Cover must be between ₹${min.toLocaleString("en-IN")} and ₹${max.toLocaleString("en-IN")}.`,
    };
  }
  const q = quantizeRiderSa(parsed, min, max);
  if (q == null) {
    return { ok: false, value: null, error: "Enter a valid cover amount." };
  }
  return { ok: true, value: q, error: null };
};
